import { useEffect, useRef } from "react";
import { useState } from "react";
import { Column, createRecord, ResultTable, ScanRecord, addRecordWithLimit, useMaxRecords } from "./ResultTableNew";
import { useScanQueueConsumer } from "./useScanQueueConsumer";
import { parseException, showError, showRepeatedScanError, parseExceptionMessageKey, clearCurrentError, clearCurrentWarning } from "utils/newExceptionUtils";
import { ScanInput } from "./ScanInput";
import { isTrackingNumber, isValidBarCode, showBarCodeError, isBagNumber } from "../../../utils/BarCodeUtil";
import { playScanFailedAoSound } from "../../../utils/scanUtils";
import { t } from '../../../i18n';

export interface HandleScanEventResult {
    success: boolean;
    messageKeyStr?: string;
}

type ArrivalScanComponentProps = {
    value1Placeholder: string;
    value1ColumnTitle: string;
    handleScanEvent: (barCode: string) => Promise<HandleScanEventResult>;
    /**
     * 可选，扩展成功记录的额外字段（如 [{ title: 'Type', key: 'type' }]）
     */
    successExtraFields?: { title: string; key: string }[];
    /**
     * 是否开启同时称重模式
     */
    weighSimultaneously?: boolean;
    /**
     * 当前重量数据
     */
    currentWeight?: {weight: number, unit: string} | null;
    /**
     * 蓝牙连接状态
     */
    isBluetoothConnected?: boolean;
}

export const ArrivalScanWightComponent: React.FC<ArrivalScanComponentProps> = ({
    value1Placeholder,
    value1ColumnTitle,
    handleScanEvent,
    successExtraFields,
    weighSimultaneously,
    currentWeight,
    isBluetoothConnected
}) => {
    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [totalSuccessCount, setTotalSuccessCount] = useState(0);
    const [totalFailedCount, setTotalFailedCount] = useState(0);
    const [value1, setValue1] = useState('');

    // 使用自定义 Hook 获取最大记录数配置
    const maxRecords = useMaxRecords();

    // 使用 ref 捕获最新的 props / state，供 onProcess 闭包读取
    const handleScanEventRef = useRef(handleScanEvent);
    handleScanEventRef.current = handleScanEvent;
    const successExtraFieldsRef = useRef(successExtraFields);
    successExtraFieldsRef.current = successExtraFields;
    const weighSimultaneouslyRef = useRef(weighSimultaneously);
    weighSimultaneouslyRef.current = weighSimultaneously;
    const currentWeightRef = useRef(currentWeight);
    currentWeightRef.current = currentWeight;
    const maxRecordsRef = useRef(maxRecords);
    maxRecordsRef.current = maxRecords;

    /** 按 seq 降序插入记录，保留最多 maxSize 条 */
    const insertBySeq = (prev: ScanRecord[], record: ScanRecord, maxSize: number): ScanRecord[] => {
        const seq = (record as any).seq ?? 0;
        const idx = prev.findIndex(r => ((r as any).seq ?? 0) < seq);
        const result = idx === -1 ? [...prev, record] : [...prev.slice(0, idx), record, ...prev.slice(idx)];
        return result.slice(0, maxSize);
    };

    const { enqueueScan } = useScanQueueConsumer({
        maxConcurrency: 5,
        onProcess: async (event) => {
            const barCode = event.code;

            // 清除之前的错误Toast
            clearCurrentError();
            clearCurrentWarning();

            try {
                const result = await handleScanEventRef.current(barCode);
                if (result.success) {
                    // 动态合并额外字段
                    const extra: Record<string, any> = {};
                    const fields = successExtraFieldsRef.current;
                    if (fields) {
                        const resultAny = result as any;
                        fields.forEach(f => {
                            if (resultAny[f.key] !== undefined) extra[f.key] = resultAny[f.key];
                        });
                    }
                    // 如果开启了同时称重且不是包牌，添加重量信息
                    if (weighSimultaneouslyRef.current && currentWeightRef.current && !isBagNumber(barCode)) {
                        extra.weight = `${currentWeightRef.current.weight} ${currentWeightRef.current.unit}`;
                    }
                    const record = { ...createRecord({ value1: barCode, ...extra }), seq: event.seq } as ScanRecord;
                    setSuccessRecords(prev => insertBySeq(prev, record, maxRecordsRef.current));
                    setTotalSuccessCount(prev => prev + 1);
                }
            } catch (error) {
                const messageKey = parseExceptionMessageKey(error);
                let message: string;
                if (messageKey === "errors.business.alreadyScanned") {
                    showRepeatedScanError(error);
                    const { message: errorMessage } = parseException(error);
                    message = errorMessage;
                } else {
                    const {title, message: errorMessage} = parseException(error);
                    playScanFailedAoSound();
                    showError({title, message: errorMessage});
                    message = errorMessage;
                }
                const record = { ...createRecord({value1: barCode, failedReason: message}), seq: event.seq } as ScanRecord;
                setFailedRecords(prev => insertBySeq(prev, record, maxRecordsRef.current));
                setTotalFailedCount(prev => prev + 1);
            }
        },
    });

    // 当同时称重开关变化时，清空已扫描列表
    useEffect(() => {
        setSuccessRecords([]);
        setFailedRecords([]);
    }, [weighSimultaneously]);

    // 组件卸载时清理弹窗
    useEffect(() => {
        return () => {
            // 清理所有可能显示的错误和警告弹窗
            clearCurrentError();
            clearCurrentWarning();
        };
    }, []);

    const handleScan = (barCode: string) => {
        // 兼容收起键盘
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        setValue1(barCode);

        if (isValidBarCode(barCode)) {
            enqueueScan(barCode);
        } else {
            showBarCodeError(barCode);
        }
    };

    // 动态拼接成功列
    const successColumns: Column[] = [
        { title: value1ColumnTitle, key: 'value1' },
        ...(successExtraFields || []),
        { title: t('pda.common.time'), key: 'time' }
    ];

    const failedColumns = [
        { title: value1ColumnTitle, key: 'value1' },
        { title: t('pda.common.time'), key: 'time' },
        {title: t('pda.common.failedReason'), key: 'failedReason'}
    ];

    return (
        <div>
            {/* 同时称重标签和蓝牙状态提示 */}
            {weighSimultaneously ? (
                <div className="bg-gray-50 px-4 py-2 rounded-lg mb-2">
                    <div className="text-sm text-green-600 mb-1 text-center">{t('pda.pages.pickUpArrival.weighSimultaneously')}</div>
                    {!isBluetoothConnected && (
                        <div className="flex items-center justify-center text-orange-500 text-sm">
                            <span className="mr-1">⚠️</span>
                            <span>{t('pda.pages.pickUpArrival.bluetoothNotConnected')}</span>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-gray-50 px-4 py-2 rounded-lg mb-2">
                    <div className="text-sm text-green-600 text-center">{t('pda.pages.pickUpArrival.notWeighing')}</div>
                </div>
            )}

            <ScanInput placeholder={value1Placeholder} value={value1} onScan={handleScan}/>

            <ResultTable
                successRecords={successRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
                totalSuccessCount={totalSuccessCount}
                totalFailedCount={totalFailedCount}
            />
        </div>
    );
};
